/**
 * Product-style hero visuals — stylised TurfAI screens, not stock diagrams.
 *
 * Every visual shows something the platform really does (a run with an approval gate,
 * Data Shield tokenisation, a chart-audit queue, the connector catalogue). TABS rules:
 * tokens only, flat, no shadows; Vibrant Orange marks the one thing that needs a person.
 * Rows "tick in" once on mount (.tick-in); reduced motion shows the final state.
 *
 * Content is illustrative sample data — names are fictional, figures match published
 * outcomes on ariviti.com where a figure appears.
 */
import { Check, KeyRound, ShieldCheck, ArrowRight, MessageSquare } from "lucide-react";
import { getPlatformTopic } from "../data/platform";
import type { ReactNode } from "react";

const delay = ["", "[animation-delay:150ms]", "[animation-delay:300ms]", "[animation-delay:450ms]", "[animation-delay:600ms]", "[animation-delay:750ms]", "[animation-delay:900ms]"];

function Frame({ children, tone = "light", className = "" }: { children: ReactNode; tone?: "light" | "dark" | "tint"; className?: string }) {
  const toneClass =
    tone === "dark" ? "bg-dark-indigo border-dark-indigo text-white" : tone === "tint" ? "bg-indigo-tint-05 border-indigo-tint-05" : "bg-white border-softblack-tint-20";
  return (
    <div className={`rounded-3xl border p-6 lg:p-7 ${toneClass} ${className}`}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ run console */

export type StepState = "done" | "wait" | "todo";
export interface RunStep {
  name: string;
  detail?: string;
  time?: string;
  state: StepState;
}

export function RunConsole({
  title,
  run,
  steps,
  gate,
  footer = "Every step logged to the audit trail",
}: {
  title: string;
  run: string;
  steps: RunStep[];
  gate?: { who: string; role: string };
  footer?: string;
}) {
  return (
    <Frame tone="dark">
      <div className="flex items-baseline justify-between gap-4 border-b border-royal-indigo pb-3.5">
        <span className="font-display font-semibold text-[17px]">{title}</span>
        <span className="flex items-center gap-2 text-[13px] text-indigo-tint-20 whitespace-nowrap">
          <span className="h-2 w-2 rounded-full bg-indigo-tint-50" aria-hidden="true" />
          {run}
        </span>
      </div>
      <ol className="relative mt-4">
        <span className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-royal-indigo" aria-hidden="true" />
        {steps.map((s, i) => (
          <li key={s.name} className={`tick-in ${delay[i] ?? ""} relative grid grid-cols-[24px_1fr_auto] gap-3.5 py-1.5`}>
            <span
              className={`relative z-[1] grid h-6 w-6 place-items-center rounded-full ${
                s.state === "done"
                  ? "bg-indigo-tint-20 text-dark-indigo"
                  : s.state === "wait"
                    ? "bg-vibrant-orange"
                    : "bg-dark-indigo border-2 border-royal-indigo"
              }`}
              aria-hidden="true"
            >
              {s.state === "done" && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
            </span>
            <div>
              <div className={`text-[15px] font-semibold ${s.state === "todo" ? "text-indigo-tint-50" : "text-white"}`}>{s.name}</div>
              {s.detail && <div className="mt-0.5 text-[13px] text-indigo-tint-20">{s.detail}</div>}
            </div>
            <span className="text-xs tabular-nums text-indigo-tint-20">{s.time}</span>
            {s.state === "wait" && gate && (
              <div className="col-span-3 ml-[38px] mt-1.5 rounded-lg bg-white p-3.5 text-soft-black">
                <div className="text-sm">
                  Assigned to <b className="text-dark-indigo">{gate.who}</b>, {gate.role}
                </div>
                <div className="mt-2.5 flex gap-2">
                  <span className="rounded bg-royal-indigo px-3 py-1.5 font-display text-[13px] font-semibold text-white">Approve</span>
                  <span className="rounded border border-royal-indigo px-3 py-1.5 font-display text-[13px] font-semibold text-royal-indigo">Send back</span>
                </div>
              </div>
            )}
          </li>
        ))}
      </ol>
      <div className="mt-3 border-t border-royal-indigo pt-3.5 text-xs text-indigo-tint-20">{footer}</div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ chart audit queue */

export function ChartAuditQueue() {
  const rows = [
    { id: "CH-20931", type: "Home health · OASIS", decision: "Compliant", conf: 98, bar: "w-[98%]", state: "done" },
    { id: "CH-20932", type: "Hospice · recert", decision: "Compliant", conf: 96, bar: "w-[96%]", state: "done" },
    { id: "CH-20933", type: "Home health · visit note", decision: "Missing signature", conf: 91, bar: "w-[91%]", state: "done" },
    { id: "CH-20934", type: "Therapy · plan of care", decision: "Unclear frequency", conf: 62, bar: "w-[62%]", state: "wait" },
    { id: "CH-20935", type: "Home health · OASIS", decision: "Compliant", conf: 97, bar: "w-[97%]", state: "done" },
  ];
  return (
    <Frame>
      <div className="flex items-baseline justify-between border-b border-softblack-tint-20 pb-4">
        <span className="font-display text-[17px] font-semibold text-dark-indigo">Chart audit queue</span>
        <span className="text-[13px] text-soft-black">Healthcare Domain Pack</span>
      </div>
      <div className="mt-2 grid grid-cols-[1fr_1.1fr_72px] gap-3 px-1 py-2 text-xs font-bold text-dark-indigo">
        <span>Chart</span>
        <span>AI decision</span>
        <span className="text-right">Confidence</span>
      </div>
      <ul>
        {rows.map((r, i) => (
          <li key={r.id} className={`tick-in ${delay[i]} grid grid-cols-[1fr_1.1fr_72px] items-center gap-3 border-t border-softblack-tint-20 px-1 py-2.5 text-[13px]`}>
            <span>
              <span className="block font-semibold text-dark-indigo">{r.id}</span>
              <span className="text-soft-black">{r.type}</span>
            </span>
            <span className="flex items-center gap-2 text-soft-black">
              <span className={`h-2 w-2 shrink-0 rounded-full ${r.state === "wait" ? "bg-vibrant-orange" : r.decision === "Compliant" ? "bg-success-text" : "bg-warning-text"}`} aria-hidden="true" />
              {r.state === "wait" ? <b className="text-dark-indigo">Clinician review</b> : r.decision}
            </span>
            <span className="flex items-center justify-end gap-2 tabular-nums">
              <span className="h-1.5 w-8 rounded-full bg-softblack-tint-20">
                <span className={`block h-1.5 rounded-full ${r.bar} ${r.conf >= 90 ? "bg-royal-indigo" : "bg-indigo-tint-50"}`} />
              </span>
              {r.conf}%
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-2 border-t border-softblack-tint-20 pt-3 text-xs text-soft-black">
        Below 85% confidence, the chart goes to a clinician with the reasoning attached.
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ data shield */

export function DataShieldCard() {
  const rows = [
    ["Name", "Asha Verma", "PERSON_7f2a"],
    ["PAN", "ABCDE1234F", "PAN_c95e"],
    ["Email", "asha@acme.in", "EMAIL_84d0"],
    ["Account", "0041 2299 8812", "ACCT_31bb"],
  ];
  return (
    <Frame>
      <div className="flex items-baseline justify-between border-b border-softblack-tint-20 pb-4">
        <span className="font-display text-[17px] font-semibold text-dark-indigo">Data Shield</span>
        <span className="flex items-center gap-1.5 text-[13px] text-success-text font-semibold">
          <ShieldCheck className="h-4 w-4" /> 4 values shielded
        </span>
      </div>
      <div className="mt-3 grid grid-cols-[56px_minmax(0,1fr)_16px_minmax(0,1fr)] sm:grid-cols-[72px_1fr_24px_1fr] items-center gap-x-3 gap-y-0 text-[13px]">
        <span />
        <span className="py-2 text-xs font-bold text-dark-indigo">In your system</span>
        <span />
        <span className="py-2 text-xs font-bold text-dark-indigo">What the model sees</span>
        {rows.map(([k, raw, tok], i) => (
          <div key={k} className={`tick-in ${delay[i]} col-span-4 grid grid-cols-[56px_minmax(0,1fr)_16px_minmax(0,1fr)] sm:grid-cols-[72px_1fr_24px_1fr] items-center gap-3 border-t border-softblack-tint-20 py-2.5`}>
            <span className="text-soft-black">{k}</span>
            <span className="font-semibold text-dark-indigo">{raw}</span>
            <ArrowRight className="h-4 w-4 text-indigo-tint-50" />
            <span className="break-all rounded bg-indigo-tint-05 px-1.5 py-1 font-semibold text-royal-indigo sm:px-2">{tok}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3 rounded-lg bg-dark-indigo p-4 text-white">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-vibrant-orange">
          <KeyRound className="h-4 w-4" />
        </span>
        <span className="text-[13px] leading-snug">
          The reversal key stays in <b>your KMS</b> — not ours, not the model provider's. Answers are restored on the way back.
        </span>
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ connector wall */

const connectorCount = (getPlatformTopic("integrations")?.connectorGroups ?? []).reduce((n, g) => n + g.items.length, 0);

export function ConnectorWall() {
  const left = ["Google Drive", "Gmail", "Microsoft 365", "Salesforce", "Slack"];
  const right = ["GitHub", "Postgres", "Snowflake", "Jira", "WhatsApp"];
  const Chip = ({ n, i }: { n: string; i: number }) => (
    <span className={`tick-in ${delay[i]} rounded-lg border border-softblack-tint-20 bg-white px-3 py-2 text-[13px] font-semibold text-dark-indigo`}>{n}</span>
  );
  return (
    <Frame tone="tint">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <div className="flex flex-col gap-2">{left.map((n, i) => <Chip key={n} n={n} i={i} />)}</div>
        <div className="relative flex flex-col items-center">
          <span className="absolute left-[-16px] right-[-16px] top-1/2 h-0.5 bg-indigo-tint-50" aria-hidden="true" />
          <div className="relative z-[1] rounded-2xl bg-dark-indigo px-5 py-6 text-center text-white">
            <div className="font-display text-[22px] font-bold">TurfAI</div>
            <div className="mt-1 text-xs text-indigo-tint-20">MCP hub</div>
            <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-vibrant-orange" aria-hidden="true" />
          </div>
        </div>
        <div className="flex flex-col gap-2">{right.map((n, i) => <Chip key={n} n={n} i={i} />)}</div>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2 text-center text-xs text-soft-black">
        <span className="rounded bg-white py-2"><b className="block font-display text-[18px] text-dark-indigo">{connectorCount}</b>connectors</span>
        <span className="rounded bg-white py-2"><b className="block font-display text-[18px] text-dark-indigo">19</b>MCP tools served</span>
        <span className="rounded bg-white py-2"><b className="block font-display text-[18px] text-dark-indigo">Any</b>REST API</span>
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ agent pipeline */

export function AgentPipeline() {
  const lane = [
    { k: "Agent", v: "Extract" },
    { k: "Agent", v: "Decide" },
    { k: "Named human", v: "Approve", gate: true },
    { k: "Agent", v: "Act" },
  ];
  const trace = [
    ["Extract", "Read 14 pages, 38 fields"],
    ["Decide", "Routed to credit review · 3.2k tokens"],
    ["Approve", "Priya Raman approved with a note"],
    ["Act", "Wrote the decision to the loan system"],
  ];
  return (
    <Frame tone="tint">
      <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <span className="absolute left-[6%] right-[6%] top-1/2 hidden h-0.5 bg-indigo-tint-50 sm:block" aria-hidden="true" />
        {lane.map((a) => (
          <div key={a.v} className={`relative min-w-0 rounded-lg border p-3 ${a.gate ? "bg-dark-indigo border-dark-indigo" : "bg-white border-softblack-tint-20"}`}>
            <div className={`text-xs ${a.gate ? "text-indigo-tint-20" : "text-soft-black"}`}>{a.k}</div>
            <div className={`font-display text-base font-semibold ${a.gate ? "text-white" : "text-dark-indigo"}`}>{a.v}</div>
            {a.gate && <span className="absolute -right-1.5 -top-1.5 h-3 w-3 rounded-full bg-vibrant-orange" aria-hidden="true" />}
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-softblack-tint-20 bg-white text-[13px]">
        <div className="grid grid-cols-[72px_1fr_auto] gap-3 px-3.5 py-2.5 font-bold text-dark-indigo">
          <span>Step</span><span>What happened</span><span>Status</span>
        </div>
        {trace.map(([s, d], i) => (
          <div key={s} className={`tick-in ${delay[i]} grid grid-cols-[72px_1fr_auto] gap-3 border-t border-softblack-tint-20 px-3.5 py-2.5`}>
            <span className="text-soft-black">{s}</span>
            <span className="text-soft-black">{d}</span>
            <span className="font-semibold text-success-text">● Done</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ squad board */

export function SquadBoard() {
  const posts = [
    ["Intake agent", "Claim pack parsed · 9 documents"],
    ["Policy agent", "Cover confirmed · clause 4.2 applies"],
    ["Valuation agent", "Settlement range drafted"],
  ];
  return (
    <Frame tone="tint">
      <div className="flex items-baseline justify-between pb-3">
        <span className="font-display text-[17px] font-semibold text-dark-indigo">Shared blackboard</span>
        <span className="text-[13px] text-soft-black">Squad · claim settlement</span>
      </div>
      <ul className="space-y-2">
        {posts.map(([who, what], i) => (
          <li key={who} className={`tick-in ${delay[i]} flex items-start gap-3 rounded-lg border border-softblack-tint-20 bg-white p-3 text-[13px]`}>
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-indigo-tint-20 font-display text-xs font-bold text-dark-indigo">{who[0]}</span>
            <span><b className="block text-dark-indigo">{who}</b><span className="text-soft-black">{what}</span></span>
          </li>
        ))}
        <li className={`tick-in ${delay[3]} flex flex-wrap items-center justify-between gap-3 rounded-lg bg-dark-indigo p-3 text-[13px] text-white`}>
          <span className="flex items-center gap-3">
            <span className="h-3 w-3 shrink-0 rounded-full bg-vibrant-orange" aria-hidden="true" />
            <span>Draft offer waiting for <b>Claims lead</b> sign-off</span>
          </span>
          <span className="rounded bg-white px-2.5 py-1 font-display text-xs font-semibold text-dark-indigo">Review</span>
        </li>
      </ul>
    </Frame>
  );
}

/* ------------------------------------------------------------------ cited answer (RAG) */

export function CitedAnswer() {
  return (
    <Frame>
      <div className="ml-auto max-w-[80%] rounded-lg bg-indigo-tint-05 px-4 py-3 text-[14px] text-dark-indigo">
        How many days of parental leave do contractors get?
      </div>
      <div className="tick-in mt-4 rounded-lg border border-softblack-tint-20 p-4 text-[14px] leading-relaxed text-soft-black">
        Contractors on assignments longer than 12 months get <b className="text-dark-indigo">10 working days</b> of paid parental leave
        <sup className="ml-0.5 font-bold text-royal-indigo">1</sup>, which can be taken within 6 months of the birth or adoption
        <sup className="ml-0.5 font-bold text-royal-indigo">2</sup>.
      </div>
      <div className="mt-4 space-y-2 text-[13px]">
        {[
          ["1", "Leave Policy 2026.pdf", "page 7"],
          ["2", "Contractor Handbook.docx", "section 3.4"],
        ].map(([n, doc, where], i) => (
          <div key={n} className={`tick-in ${delay[i + 1]} flex items-center gap-3 rounded-lg bg-indigo-tint-05 px-3 py-2`}>
            <span className="grid h-5 w-5 place-items-center rounded-full bg-royal-indigo text-[11px] font-bold text-white">{n}</span>
            <span className="font-semibold text-dark-indigo">{doc}</span>
            <span className="text-soft-black">{where}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ pack list */

export function PackList() {
  const packs = getPlatformTopic("domain-packs")?.packs ?? [];
  return (
    <Frame>
      <ul>
        {packs.map((p, i) => {
          const live = p.status === "Live";
          return (
            <li key={p.name} className={`tick-in ${delay[Math.min(i, delay.length - 1)]} flex items-center justify-between gap-4 border-t border-softblack-tint-20 py-2.5 first:border-t-0`}>
              <b className="font-display text-[15px] text-dark-indigo">{p.name}</b>
              <span className={`flex items-center gap-1.5 whitespace-nowrap text-xs font-bold ${live ? "text-success-text" : "text-soft-black"}`}>
                <span className={`h-2 w-2 rounded-full ${live ? "bg-success-text" : "bg-indigo-tint-50"}`} aria-hidden="true" />
                {p.status}
              </span>
            </li>
          );
        })}
      </ul>
    </Frame>
  );
}

/* ------------------------------------------------------------------ chatbot widget */

export function ChatbotWidget() {
  return (
    <Frame tone="tint">
      <div className="rounded-lg border border-softblack-tint-20 bg-white">
        <div className="flex items-center justify-between border-b border-softblack-tint-20 px-4 py-3">
          <span className="flex items-center gap-2 text-[14px] font-bold text-dark-indigo">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-royal-indigo text-white"><MessageSquare className="h-3.5 w-3.5" /></span>
            Acme Support
          </span>
          <span className="text-[12px] text-soft-black">Answers from 3 collections</span>
        </div>
        <div className="space-y-3 p-4 text-[14px]">
          <div className="ml-auto max-w-[80%] rounded-lg bg-indigo-tint-05 px-3 py-2 text-dark-indigo">Can I change the delivery address after I've paid?</div>
          <div className="tick-in max-w-[90%] rounded-lg border border-softblack-tint-20 px-3 py-2 leading-relaxed text-soft-black">
            Yes — until the order is packed, from <b className="text-dark-indigo">Orders › Edit address</b><sup className="ml-0.5 font-bold text-royal-indigo">1</sup>. After that, our team can redirect it for you.
            <span className={`tick-in ${delay[1]} mt-2 flex items-center gap-2 text-[12px]`}>
              <span className="grid h-4 w-4 place-items-center rounded-full bg-royal-indigo text-[10px] font-bold text-white">1</span>
              <span className="font-semibold text-dark-indigo">Shipping FAQ</span> <span>· help centre</span>
            </span>
          </div>
          <div className={`tick-in ${delay[2]} flex items-center gap-3 rounded-lg border border-vibrant-orange px-3 py-2 text-[13px]`}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-vibrant-orange" aria-hidden="true" />
            <span className="text-soft-black">Order already packed — <b className="text-dark-indigo">handing off to a person</b> with the conversation attached.</span>
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[12px] font-semibold text-dark-indigo">
        {["Script tag", "Sandboxed iframe", "REST API"].map((e, i) => (
          <span key={e} className={`tick-in ${delay[i + 3]} rounded-md border border-indigo-tint-20 bg-white px-2 py-2`}>{e}</span>
        ))}
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ governance panel */

export function GovernancePanel() {
  const caps: [string, string, string, string][] = [
    ["Tenant · Acme Insurance", "$6,200 of $10,000", "w-[62%]", "bg-royal-indigo"],
    ["Workflow · Claims triage", "1.71M of 2M tokens", "w-[86%]", "bg-vibrant-orange"],
  ];
  return (
    <Frame>
      <div className="flex items-center justify-between">
        <b className="font-display text-[16px] text-dark-indigo">Limits this month</b>
        <span className="text-[13px] text-soft-black">Resets 1 Nov</span>
      </div>
      <div className="mt-4 space-y-4">
        {caps.map(([name, used, w, bar], i) => (
          <div key={name} className={`tick-in ${delay[i]}`}>
            <div className="flex justify-between text-[13px]">
              <span className="font-semibold text-dark-indigo">{name}</span>
              <span className="text-soft-black">{used}</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-indigo-tint-05">
              <div className={`h-2 rounded-full ${w} ${bar}`} />
            </div>
          </div>
        ))}
        <p className={`tick-in ${delay[2]} text-[13px] text-soft-black`}>Claims triage passed 80% — owner notified. New runs stop at 100%.</p>
      </div>
      <div className="mt-5 border-t border-softblack-tint-20 pt-4">
        <b className="text-[13px] text-dark-indigo">Audit log</b>
        <ul className="mt-2 space-y-2 text-[13px]">
          {[
            ["10:42", "Prompt “claims-summary v4” published", "Injection check passed"],
            ["10:15", "Failed run replayed", "Transient · provider timeout"],
            ["09:58", "Data Shield on 212 calls", "Counts only, no raw values"],
          ].map(([t, what, note], i) => (
            <li key={t} className={`tick-in ${delay[i + 3]} grid grid-cols-[44px_1fr] gap-2`}>
              <span className="text-soft-black">{t}</span>
              <span><span className="font-semibold text-dark-indigo">{what}</span><span className="block text-soft-black">{note}</span></span>
            </li>
          ))}
        </ul>
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ model scoreboard (Edge AI) */

export function ModelScoreboard() {
  const rows: [string, string, string, string, string][] = [
    ["Llama 3.2 3B", "Local · 8-bit", "w-[94%]", "94%", "0.9 s"],
    ["Qwen2.5-VL 7B", "Local · 4-bit", "w-[97%]", "97%", "1.6 s"],
    ["Cloud baseline", "Frontier API", "w-[98%]", "98%", "2.4 s"],
  ];
  return (
    <Frame>
      <div className="flex items-baseline justify-between">
        <b className="font-display text-[16px] text-dark-indigo">KYC extraction benchmark</b>
        <span className="text-[13px] text-soft-black">Your documents</span>
      </div>
      <div className="mt-4 grid grid-cols-[1fr_auto] gap-x-4 text-[12px] font-semibold text-soft-black">
        <span>Field accuracy</span>
        <span className="w-12 text-right">P95</span>
      </div>
      <ul className="mt-2 space-y-4">
        {rows.map(([name, where, w, acc, p95], i) => (
          <li key={name} className={`tick-in ${delay[i]}`}>
            <div className="flex justify-between text-[13px]">
              <span><b className="text-dark-indigo">{name}</b> <span className="text-soft-black">· {where}</span></span>
            </div>
            <div className="mt-1.5 grid grid-cols-[1fr_auto] items-center gap-x-4">
              <div className="h-2 rounded-full bg-indigo-tint-05"><div className={`h-2 rounded-full ${w} ${i === 2 ? "bg-indigo-tint-50" : "bg-royal-indigo"}`} /></div>
              <span className="w-12 text-right text-[13px] tabular-nums text-dark-indigo">{acc} · {p95}</span>
            </div>
          </li>
        ))}
      </ul>
      <div className={`tick-in ${delay[3]} mt-5 flex items-center gap-3 rounded-lg bg-dark-indigo px-4 py-3 text-[13px] text-white`}>
        <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-vibrant-orange" aria-hidden="true" />
        Local models run on your GPU servers or Jetson Orin NX — no document leaves the building.
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ eval board (PromptBench) */

export function EvalBoard() {
  const models: [string, string, string][] = [
    ["GPT-4o", "Pass · 0.93", "$4.10"],
    ["Claude Sonnet", "Pass · 0.95", "$3.60"],
    ["Gemini Flash", "Pass · 0.91", "$0.40"],
  ];
  return (
    <Frame>
      <div className="flex items-baseline justify-between">
        <b className="font-display text-[16px] text-dark-indigo">claims-summary · v3 → v4</b>
        <span className="text-[13px] text-soft-black">48 test cases</span>
      </div>
      <div className="mt-4 space-y-1.5 rounded-lg bg-indigo-tint-05 p-3.5 text-[13px] leading-relaxed text-soft-black">
        <div className="line-through">Summarise the claim in a paragraph.</div>
        <div className="rounded bg-indigo-tint-20 px-1 text-dark-indigo">Summarise the claim as JSON: cause, amount, documents missing.</div>
      </div>
      <div className="mt-4 grid grid-cols-4 gap-2 text-center text-[11.5px] text-soft-black">
        {[["0.94", "similarity"], ["5/5", "keywords"], ["100%", "valid JSON"], ["4.6", "reviewers"]].map(([v, l], i) => (
          <span key={l} className={`tick-in ${delay[i]} rounded bg-white border border-softblack-tint-20 py-2`}><b className="block font-display text-[16px] text-dark-indigo">{v}</b>{l}</span>
        ))}
      </div>
      <ul className="mt-4">
        {models.map(([m, verdict, cost], i) => (
          <li key={m} className={`tick-in ${delay[i + 4]} flex items-center justify-between border-t border-softblack-tint-20 py-2.5 text-[13px]`}>
            <b className="text-dark-indigo">{m}</b>
            <span className="flex items-center gap-1.5 font-semibold text-success-text"><Check className="h-3.5 w-3.5" strokeWidth={3} />{verdict}</span>
            <span className="flex items-center gap-2 tabular-nums text-soft-black">
              {i === 2 && <><span className="h-2 w-2 rounded-full bg-vibrant-orange" aria-hidden="true" /><span className="sr-only">Cheapest pass:</span></>}
              {cost} / 1k runs
            </span>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

/* ------------------------------------------------------------------ mentor chat (learning tools) */

export function MentorChat() {
  return (
    <Frame tone="tint">
      <div className="rounded-lg border border-softblack-tint-20 bg-white">
        <div className="flex items-center justify-between border-b border-softblack-tint-20 px-4 py-3">
          <span className="text-[14px] font-bold text-dark-indigo">FishBone mentor</span>
          <span className="rounded-full bg-indigo-tint-20 px-2.5 py-0.5 text-[11.5px] font-semibold text-royal-indigo">Running on TurfAI</span>
        </div>
        <div className="space-y-3 p-4 text-[14px]">
          <div className="ml-auto max-w-[82%] rounded-lg bg-indigo-tint-05 px-3 py-2 text-dark-indigo">My model's accuracy is 99% but it misses every fraud case. Why?</div>
          <div className="tick-in max-w-[90%] rounded-lg border border-softblack-tint-20 px-3 py-2 leading-relaxed text-soft-black">
            Good catch. Before we fix it — <b className="text-dark-indigo">what share of your rows are fraud?</b> What would a model that always says “not fraud” score?
          </div>
          <div className={`tick-in ${delay[1]} flex flex-wrap gap-2`}>
            <span className="rounded-full border border-royal-indigo px-3 py-1 text-[12.5px] font-semibold text-royal-indigo">Give me a hint</span>
            <span className="rounded-full border border-softblack-tint-20 px-3 py-1 text-[12.5px] text-soft-black">Path A · class balance</span>
            <span className="rounded-full border border-softblack-tint-20 px-3 py-1 text-[12.5px] text-soft-black">Path B · metrics</span>
          </div>
          <div className={`tick-in ${delay[2]} flex items-center gap-3 rounded-lg border border-vibrant-orange px-3 py-2 text-[13px]`}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-vibrant-orange" aria-hidden="true" />
            <span className="text-soft-black">Misconception spotted: <b className="text-dark-indigo">accuracy on imbalanced data</b> — flagged for the instructor.</span>
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[12px] font-semibold text-dark-indigo">
        {["SkillUp", "DataLab", "FishBone"].map((e, i) => (
          <span key={e} className={`tick-in ${delay[i + 3]} rounded-md border border-indigo-tint-20 bg-white px-2 py-2`}>{e}</span>
        ))}
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ rails stack (Why TurfAI) */

export function RailsStack() {
  const rails: [string, string][] = [
    ["Identity & access", "RBAC, SSO, per-tenant isolation"],
    ["Data Shield", "Personal data tokenised before the model"],
    ["Orchestration", "Workflows, agents, squads, retries"],
    ["Human-in-the-loop", "Named approvers at every risky step"],
    ["Audit & cost", "Every step logged, costed, attributable"],
  ];
  return (
    <Frame tone="tint">
      <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-3">
        <div className="flex flex-col justify-center gap-2">
          {["Your systems", "Your people", "Your documents"].map((t, i) => (
            <span key={t} className={`tick-in ${delay[i]} rounded-lg border border-softblack-tint-20 bg-white px-3 py-2 text-center text-[13px] font-semibold text-dark-indigo`}>{t}</span>
          ))}
        </div>
        <div className="flex w-[200px] flex-col gap-1.5 rounded-xl bg-dark-indigo p-3">
          <div className="pb-1 text-center font-display text-[15px] font-bold text-white">TurfAI rails</div>
          {rails.map(([t, d], i) => (
            <div key={t} className={`tick-in ${delay[i + 1]} rounded-md bg-royal-indigo px-2.5 py-1.5`}>
              <div className="text-[12.5px] font-semibold text-white">{t}</div>
              <div className="text-[11px] leading-snug text-indigo-tint-20">{d}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-col justify-center gap-2">
          {["Claude", "GPT", "Gemini", "Open models"].map((t, i) => (
            <span key={t} className={`tick-in ${delay[i + 2]} rounded-lg border border-softblack-tint-20 bg-white px-3 py-2 text-center text-[13px] font-semibold text-dark-indigo`}>{t}</span>
          ))}
        </div>
      </div>
      <p className="mt-4 flex items-center gap-2 text-[13px] text-soft-black">
        <span className="h-2.5 w-2.5 rounded-full bg-vibrant-orange" aria-hidden="true" />
        The model is one box. Production needs everything around it.
      </p>
    </Frame>
  );
}

/* ------------------------------------------------------------------ industry grid (Solutions) */

export function IndustryGrid({ items }: { items: { name: string; line: string }[] }) {
  return (
    <Frame>
      <div className="flex items-baseline justify-between">
        <b className="font-display text-[16px] text-dark-indigo">Live on TurfAI</b>
        <span className="text-[13px] text-soft-black">{items.length} industries</span>
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-2">
        {items.map((it, i) => (
          <li key={it.name} className={`tick-in ${delay[Math.min(i, delay.length - 1)]} rounded-lg border border-softblack-tint-20 px-3 py-2.5`}>
            <span className="flex items-center gap-1.5 text-[14px] font-semibold text-dark-indigo">
              <span className="h-2 w-2 rounded-full bg-success-text" aria-hidden="true" />{it.name}
            </span>
            <span className="mt-0.5 block text-[12px] leading-snug text-soft-black">{it.line}</span>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

/* ------------------------------------------------------------------ case snapshot (case studies) */

export function CaseSnapshot({ sector, rows, capabilities }: { sector: string; rows: { label: string; before: string; after: string }[]; capabilities: string[] }) {
  return (
    <Frame>
      <div className="flex items-baseline justify-between">
        <b className="font-display text-[16px] text-dark-indigo">Before and after TurfAI</b>
        <span className="text-[13px] text-soft-black">{sector}</span>
      </div>
      <div className="mt-4 grid grid-cols-[1.1fr_1fr_1fr] gap-x-3 border-b border-softblack-tint-20 pb-2 text-[12px] font-semibold text-soft-black">
        <span>Measure</span><span>Before</span><span className="text-royal-indigo">With TurfAI</span>
      </div>
      <ul>
        {rows.map((r, i) => (
          <li key={r.label} className={`tick-in ${delay[i]} grid grid-cols-[1.1fr_1fr_1fr] gap-x-3 border-b border-softblack-tint-20 py-2.5 text-[13px] last:border-b-0`}>
            <span className="font-semibold text-dark-indigo">{r.label}</span>
            <span className="text-soft-black">{r.before}</span>
            <span className="font-semibold text-dark-indigo">{r.after}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {capabilities.map((c, i) => (
          <span key={c} className={`tick-in ${delay[Math.min(i + 3, delay.length - 1)]} rounded-full bg-indigo-tint-20 px-2.5 py-1 text-[12px] font-semibold text-royal-indigo`}>{c}</span>
        ))}
      </div>
    </Frame>
  );
}

/* ------------------------------------------------------------------ presets */

export const runs = {
  loan: {
    title: "Loan file · Acme Traders",
    run: "Run 4821",
    gate: { who: "Priya Raman", role: "Credit officer" },
    steps: [
      { name: "Document received", detail: "Webhook · 14-page PDF", time: "09:41:02", state: "done" },
      { name: "Data Shield", detail: "6 personal values tokenised before the model", time: "09:41:03", state: "done" },
      { name: "Extraction agent", detail: "38 fields · 2 below confidence, queued", time: "09:41:11", state: "done" },
      { name: "Credit memo agent", detail: "Draft memo ready for review", time: "09:41:26", state: "done" },
      { name: "Credit officer approval", detail: "The agent proposes. A named person decides.", time: "waiting", state: "wait" },
      { name: "Write to loan system", state: "todo" },
    ] as RunStep[],
  },
  incident: {
    title: "Incident · checkout-api latency",
    run: "Run 1187",
    gate: { who: "On-call SRE", role: "Platform team" },
    steps: [
      { name: "Alerts correlated", detail: "41 alerts from 3 tools → 1 incident", time: "02:13:40", state: "done" },
      { name: "Root cause analysis", detail: "Disk 96% on node db-07 · evidence attached", time: "02:13:52", state: "done" },
      { name: "Auto-remediation", detail: "Playbook: disk clean-up · freed 38 GB", time: "02:14:20", state: "done" },
      { name: "Config rollback proposed", detail: "Outside playbook — needs a person", time: "waiting", state: "wait" },
      { name: "Morning report", state: "todo" },
    ] as RunStep[],
  },
  hr: {
    title: "HR ticket · leave policy question",
    run: "Run 3302",
    gate: { who: "Meera Iyer", role: "HR business partner" },
    steps: [
      { name: "Ticket received", detail: "Slack · employee self-service", time: "11:02:10", state: "done" },
      { name: "Policy answer drafted", detail: "Cited: Leave Policy 2026, page 7", time: "11:02:13", state: "done" },
      { name: "Exception detected", detail: "Request falls outside the written policy", time: "11:02:14", state: "done" },
      { name: "HR review", detail: "Policy needs interpreting — a person decides", time: "waiting", state: "wait" },
      { name: "Reply to employee", state: "todo" },
    ] as RunStep[],
  },
  claims: {
    title: "Claim · life policy, death claim",
    run: "Claim 6074",
    gate: { who: "Daniel Okafor", role: "Claims officer" },
    steps: [
      { name: "Claim documents received", detail: "Claim form, death certificate, medical record, bank proof", time: "08:12:04", state: "done" },
      { name: "Data Shield", detail: "Policyholder and nominee details tokenised", time: "08:12:05", state: "done" },
      { name: "Intake agents", detail: "Captured, screened, validated · policy in force", time: "08:12:19", state: "done" },
      { name: "Fraud indicators", detail: "None found · acknowledgement sent to nominee", time: "08:13:02", state: "done" },
      { name: "Settlement squad", detail: "Evidence, coverage, valuation, draft, compliance — officer signs", time: "waiting", state: "wait" },
      { name: "Bank verified, claim paid", state: "todo" },
    ] as RunStep[],
  },
  assessment: {
    title: "Assessment · Senior backend engineer",
    run: "Candidate 2214",
    gate: { who: "Ananya Rao", role: "Recruiter" },
    steps: [
      { name: "Resume parsed", detail: "Match score 82 · HR 78 · technical 86", time: "10:04:12", state: "done" },
      { name: "Adaptive test", detail: "14 questions · difficulty rose to level 4", time: "10:31:40", state: "done" },
      { name: "Coding answer checked", detail: "Ran against 12 test cases · 11 passed", time: "10:33:05", state: "done" },
      { name: "Integrity read-out", detail: "2 moments flagged for review", time: "10:33:09", state: "done" },
      { name: "Shortlist decision", detail: "Recommendation: advance — a recruiter decides", time: "waiting", state: "wait" },
      { name: "Panel interview booked", state: "todo" },
    ] as RunStep[],
  },
  legal: {
    title: "Matter · reply to petition",
    run: "Draft 318",
    gate: { who: "Adv. K. Menon", role: "Counsel" },
    steps: [
      { name: "Petition uploaded", detail: "42 pages · 63 numbered paragraphs", time: "14:02:10", state: "done" },
      { name: "Preliminary objections", detail: "Maintainability and limitation drafted", time: "14:03:02", state: "done" },
      { name: "Para-by-para reply", detail: "63 of 63 paragraphs answered", time: "14:05:47", state: "done" },
      { name: "Prayer drafted", detail: "In the firm's standard structure", time: "14:06:01", state: "done" },
      { name: "Counsel review", detail: "Edit and approve — nothing is filed by the system", time: "waiting", state: "wait" },
      { name: "Export to Word", state: "todo" },
    ] as RunStep[],
  },
  telecom: {
    title: "Template · promotional SMS (Hindi)",
    run: "Check 90412",
    gate: { who: "Registrar desk", role: "First-level review" },
    steps: [
      { name: "Template received", detail: "Bulk API · 1 of 240", time: "09:12:01", state: "done" },
      { name: "Data Shield", detail: "Brand and sender ID tokenised", time: "09:12:01", state: "done" },
      { name: "Category check", detail: "Declared transactional · detected promotional", time: "09:12:02", state: "done" },
      { name: "Rule checks", detail: "Opt-out wording missing · score 42/100", time: "09:12:02", state: "done" },
      { name: "Registrar decision", detail: "Recommend Return, with fix and clause cited", time: "waiting", state: "wait" },
      { name: "Notify the sender", state: "todo" },
    ] as RunStep[],
  },
  manufacturing: {
    title: "Shift log · Line 3, night shift",
    run: "Report 1127",
    gate: { who: "Production manager", role: "Plant A" },
    steps: [
      { name: "Handwritten log scanned", detail: "2 pages · 48 rows read", time: "06:10:44", state: "done" },
      { name: "Uncertain cells flagged", detail: "3 cells for the manager to check", time: "06:11:02", state: "done" },
      { name: "Rejects analysed", detail: "41 rejects · 70% from one setup step", time: "06:11:30", state: "done" },
      { name: "Training matched", detail: "2 operators · fixture setup module", time: "06:11:41", state: "done" },
      { name: "Manager approval", detail: "Approve the report to assign training", time: "waiting", state: "wait" },
      { name: "Email operators", state: "todo" },
    ] as RunStep[],
  },
  admissions: {
    title: "Admissions · cut-off for M.Tech",
    run: "Cycle 2026",
    gate: { who: "Dean's office", role: "Checker" },
    steps: [
      { name: "Applications read", detail: "4,812 documents · 96% fields high-confidence", time: "11:20:05", state: "done" },
      { name: "Low-confidence fields", detail: "184 sent to reviewers, resolved", time: "13:48:10", state: "done" },
      { name: "Rule pack v12 applied", detail: "Eligibility, merit and reservation — no AI", time: "13:52:31", state: "done" },
      { name: "Cut-off proposed", detail: "Prepared by the admissions office (maker)", time: "13:52:44", state: "done" },
      { name: "Cut-off approval", detail: "Checker approves — decision record sealed", time: "waiting", state: "wait" },
      { name: "Offers released", state: "todo" },
    ] as RunStep[],
  },
  invoice: {
    title: "Invoice · supplier batch",
    run: "Batch 772",
    gate: { who: "AP reviewer", role: "Finance" },
    steps: [
      { name: "Received by email", detail: "Gmail → vendor folder · 18 invoices", time: "08:40:02", state: "done" },
      { name: "Fields extracted", detail: "Line items, totals, tax IDs", time: "08:40:31", state: "done" },
      { name: "Checks run", detail: "Totals add up · GSTIN valid · QR matched", time: "08:40:33", state: "done" },
      { name: "Straight through", detail: "15 of 18 approved automatically", time: "08:40:34", state: "done" },
      { name: "3 exceptions", detail: "Total mismatch on one line — one click to fix", time: "waiting", state: "wait" },
      { name: "Export to ERP", state: "todo" },
    ] as RunStep[],
  },
  workflow: {
    title: "Workflow · KYC onboarding",
    run: "Run 2950",
    gate: { who: "Rahul Menon", role: "Compliance officer" },
    steps: [
      { name: "Trigger", detail: "Customer uploads ID documents", time: "15:20:01", state: "done" },
      { name: "Data Shield", detail: "Identity values tokenised", time: "15:20:02", state: "done" },
      { name: "KYC agent", detail: "Fields extracted · watch-list checked", time: "15:20:09", state: "done" },
      { name: "Flag raised", detail: "Name variant needs review", time: "waiting", state: "wait" },
      { name: "Account opened", state: "todo" },
    ] as RunStep[],
  },
};
